import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { trackingApi } from '@/api/tracking';
import { clientCompanyApi } from '@/api/clientCompany';
import { useCompany } from '@/hooks/useCompany';
import { useToast } from '@/hooks/useToast';
/** Loads the tracking config and tracks its ETag header. */
function useTrackingFetcher(clientCompanyId) {
    const { showErrorUnless422 } = useToast();
    const [tracking, setTracking] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const etagRef = useRef(null);
    const hasLoadedOnce = useRef(false);
    const refetch = useCallback(async () => {
        if (!clientCompanyId)
            return;
        if (!hasLoadedOnce.current)
            setIsLoading(true);
        setError(null);
        const res = await trackingApi.getCompanyTracking(clientCompanyId);
        if (!res.data) {
            const message = res.error || 'Failed to load tracking config';
            setError(message);
            showErrorUnless422('Load failed', res, message);
            if (!hasLoadedOnce.current) {
                hasLoadedOnce.current = true;
                setIsLoading(false);
            }
            return;
        }
        setTracking(res.data);
        etagRef.current = res.etag ?? null;
        if (!hasLoadedOnce.current) {
            hasLoadedOnce.current = true;
            setIsLoading(false);
        }
    }, [clientCompanyId, showErrorUnless422]);
    useEffect(() => {
        hasLoadedOnce.current = false;
        setTracking(null);
        etagRef.current = null;
        void refetch();
    }, [refetch]);
    return useMemo(() => ({ tracking, setTracking, isLoading, error, refetch, etagRef }), [tracking, isLoading, error, refetch]);
}
/** Returns a setEnabled callback that PATCHes the toggle and refreshes the
 *  parent ClientCompany context (so the next PATCH sees the fresh ETag). */
function useToggleEnabled(clientCompanyId, fetcher) {
    const { showErrorUnless422, showSuccess } = useToast();
    const { company, updateCompany } = useCompany();
    return useCallback(async (next) => {
        if (!clientCompanyId || !fetcher.tracking)
            return;
        const previous = fetcher.tracking;
        fetcher.setTracking({ ...previous, Enabled: next });
        // API validates concurrency on the parent ClientCompany; ETag header on
        // the tracking endpoint isn't reliably exposed via CORS, so fall back to
        // the ClientCompany body DTO's ETag.
        const etag = fetcher.etagRef.current ?? company?.ETag ?? '';
        const res = await trackingApi.patchCompanyTracking(clientCompanyId, {
            ETag: etag,
            Enabled: next,
        });
        if (!res.data) {
            fetcher.setTracking(previous);
            showErrorUnless422('Update failed', res, 'Unable to update tracking');
            return;
        }
        fetcher.setTracking(res.data);
        fetcher.etagRef.current = res.etag ?? fetcher.etagRef.current;
        showSuccess('Tracking updated', next ? 'Visitor tracking is now enabled.' : 'Visitor tracking is now disabled.');
        // PATCH bumped ClientCompany.ETag server-side; refresh the context.
        const fresh = await clientCompanyApi.getById(clientCompanyId);
        if (fresh.data)
            updateCompany(fresh.data);
    }, [clientCompanyId, company?.ETag, fetcher, updateCompany, showErrorUnless422, showSuccess]);
}
export function useCompanyTracking(clientCompanyId) {
    const fetcher = useTrackingFetcher(clientCompanyId);
    const setEnabled = useToggleEnabled(clientCompanyId, fetcher);
    return {
        tracking: fetcher.tracking,
        isLoading: fetcher.isLoading,
        error: fetcher.error,
        refetch: fetcher.refetch,
        setEnabled,
    };
}
